import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import {
  BLACKLIST_PLATFORM_LABELS,
  BLACKLIST_REASONS,
  BlacklistEntry,
  BlacklistKey,
  BlacklistPrepareResult,
  BlacklistReason,
  BlacklistSubject,
  customerBlacklistService,
} from '../services/customerBlacklistService';

export interface AddToBlacklistModalProps {
  open: boolean;
  onClose: () => void;
  /** Siparişten ekleme. null => elle ekleme. entryId varsa yok sayılır. */
  subject: BlacklistSubject | null;
  /** Paket ekranı şubesi; şirket görünümünde null */
  branchId: string | null;
  branchName?: string | null;
  /** Yeni kayıt yerine mevcut kaydı düzenle */
  entryId?: number | null;
  onSaved: (entry: BlacklistEntry) => void;
  /** Müşteri zaten listedeyse o kaydı aç */
  onOpenEntry?: (entryId: number) => void;
}

/** Kara liste vurgu rengi (siyah) */
export const BLACK = '#1a1a1a';
export const blackButtonSx = {
  bgcolor: BLACK,
  color: '#fff',
  '&:hover': { bgcolor: '#000' },
  '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
} as const;

const keyId = (key: Pick<BlacklistKey, 'type' | 'value'>) => `${key.type}|${key.value}`;

export const formatBlacklistDate = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const AddToBlacklistModal: React.FC<AddToBlacklistModalProps> = ({
  open, onClose, subject, branchId, branchName, entryId, onSaved, onOpenEntry,
}) => {
  const { t } = useTranslation('salesSystem');
  const k = (key: string, o?: Record<string, unknown>) => t(`orderManagement.blacklist.${key}`, o);

  const mode: 'edit' | 'order' | 'manual' = entryId ? 'edit' : subject ? 'order' : 'manual';

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [prep, setPrep] = useState<BlacklistPrepareResult | null>(null);
  const [entry, setEntry] = useState<BlacklistEntry | null>(null);
  const [existing, setExisting] = useState<BlacklistEntry | null>(null);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState<BlacklistReason | null>(null);
  const [note, setNote] = useState('');
  const [onlyBranch, setOnlyBranch] = useState(false);

  // Düzenleme: çıkarılacak / eklenecek tanıtıcılar
  const [removeKeyIds, setRemoveKeyIds] = useState<Set<number>>(new Set());
  const [addPhones, setAddPhones] = useState<string[]>([]);
  const [newPhone, setNewPhone] = useState('');

  // Üst ekran her çizimde yeni nesne verse de yeniden yüklenmesin
  const subjectKey = subject ? `${subject.platform}|${subject.orderRef || ''}|${subject.phone || ''}` : '';

  // Açılışta sıfırla ve yükle
  useEffect(() => {
    if (!open) return;
    setLoadError(null);
    setError(null);
    setPrep(null);
    setEntry(null);
    setExisting(null);
    setChecked(new Set());
    setName('');
    setPhone('');
    setAddress('');
    setReason(null);
    setNote('');
    setOnlyBranch(false);
    setRemoveKeyIds(new Set());
    setAddPhones([]);
    setNewPhone('');

    if (mode === 'manual') return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (mode === 'edit' && entryId) {
          const e = await customerBlacklistService.get(entryId, branchId);
          if (cancelled) return;
          setEntry(e);
          setName(e.displayName);
          setReason(e.reason);
          setNote(e.note || '');
          setOnlyBranch(e.branchId !== '');
        } else if (subject) {
          const p = await customerBlacklistService.prepare(subject, branchId);
          if (cancelled) return;
          setPrep(p);
          setName(p.displayName || subject.name || '');
          setExisting(p.existing ?? null);
          setChecked(new Set(p.keys.filter(x => x.usable).map(keyId)));
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || k('add.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryId, subjectKey, branchId]);

  const readOnly = mode === 'edit' && !!entry && !entry.canManage;
  const showScope = !!branchId || (mode === 'edit' && !!entry?.branchId);
  const scopeBranchName = branchName || entry?.branchName || '';

  const title = mode === 'edit' ? k('add.editTitle') : mode === 'manual' ? k('add.manualTitle') : k('add.title');
  const subtitle = useMemo(() => {
    if (mode === 'order' && subject) {
      const ref = (prep?.orderRef || subject.orderRef || '').slice(-10);
      return [BLACKLIST_PLATFORM_LABELS[subject.platform] || subject.platform, ref ? `#${ref}` : '', prep?.displayName || subject.name || '']
        .filter(Boolean).join(' · ');
    }
    if (mode === 'edit' && entry) {
      return [entry.ownerBranchName || k('companyOwner'), formatBlacklistDate(entry.createdAt), entry.createdByName || '']
        .filter(Boolean).join(' · ');
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, subject, prep, entry]);

  const keyHint = (key: BlacklistKey) =>
    key.type === 'phone' ? k('add.phoneHint') : key.type === 'account' ? k('add.accountHint') : k('add.strong');

  const keyDisplay = (key: BlacklistKey) => key.display || key.value;

  const toggleChecked = (id: string) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleRemove = (id: number) => setRemoveKeyIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const addPhone = () => {
    const v = newPhone.trim();
    if (!v || addPhones.includes(v)) return;
    setAddPhones(prev => [...prev, v]);
    setNewPhone('');
  };

  // ---------------- Kaydet ----------------

  const submit = async () => {
    setError(null);
    if (!reason) { setError(k('add.needReason')); return; }
    const displayName = name.trim();
    setSaving(true);
    try {
      if (mode === 'edit' && entry) {
        const saved = await customerBlacklistService.update({
          id: entry.id,
          displayName: displayName || entry.displayName,
          reason,
          note: note.trim() || null,
          onlyBranch,
          branchId: branchId || (onlyBranch ? entry.branchId : null),
          addKeys: addPhones.map(v => ({ type: 'phone', value: v })),
          removeKeyIds: Array.from(removeKeyIds),
        });
        onSaved(saved);
        return;
      }

      if (mode === 'order' && subject && prep) {
        const keys = prep.keys.filter(x => x.usable && checked.has(keyId(x)));
        if (keys.length === 0) { setError(k('add.needKey')); return; }
        const saved = await customerBlacklistService.create({
          platform: subject.platform,
          orderRef: prep.orderRef || subject.orderRef || null,
          displayName: displayName || prep.displayName,
          address: prep.address ?? subject.address ?? null,
          accountId: prep.accountId ?? subject.accountId ?? null,
          keys: keys.map(x => ({ type: x.type, value: x.value, display: x.display })),
          reason,
          note: note.trim() || null,
          onlyBranch: !!branchId && onlyBranch,
          branchId,
        });
        onSaved(saved);
        return;
      }

      // Elle ekleme: önce telefonu sunucuda doğrula
      if (!displayName) return;
      const p = await customerBlacklistService.prepare({ platform: 'phone', name: displayName, phone: phone.trim() || null, address: address.trim() || null }, branchId);
      if (p.existing) { setExisting(p.existing); return; }
      const phoneKey = p.keys.find(x => x.type === 'phone');
      if (phone.trim() && phoneKey && !phoneKey.usable) { setError(phoneKey.warning || k('add.needKey')); return; }
      const keys = p.keys.filter(x => x.usable);
      if (keys.length === 0) { setError(phoneKey?.warning || k('add.needKey')); return; }
      const saved = await customerBlacklistService.create({
        platform: 'phone',
        orderRef: null,
        displayName,
        address: address.trim() || p.address || null,
        accountId: p.accountId ?? null,
        keys: keys.map(x => ({ type: x.type, value: x.value, display: x.display })),
        reason,
        note: note.trim() || null,
        onlyBranch: !!branchId && onlyBranch,
        branchId,
      });
      onSaved(saved);
    } catch (e: any) {
      setError(e?.message || k('add.loadError'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Görünüm parçaları ----------------

  const sectionLabel = (text: string) => (
    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {text}
    </Typography>
  );

  const keyRow = (opts: { id: string; label: string; value?: string | null; hint?: string | null; warning?: string | null; checked: boolean; disabled: boolean; onToggle?: () => void; tag?: string }) => (
    <Box key={opts.id} component="label"
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.75, borderRadius: 1, border: 1, borderColor: 'divider',
        cursor: opts.disabled ? 'default' : 'pointer', opacity: opts.disabled && !opts.warning ? 0.6 : 1,
      }}>
      <Checkbox size="small" checked={opts.checked} disabled={opts.disabled} onChange={opts.onToggle} sx={{ p: 0.25, mt: 0.1 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>{opts.label}</Typography>
          {opts.value ? <Typography variant="body2" sx={{ fontFamily: MONO }}>{opts.value}</Typography> : null}
          {opts.tag ? <Chip size="small" color="warning" variant="outlined" label={opts.tag} sx={{ height: 20 }} /> : null}
        </Box>
        {opts.hint ? <Typography variant="caption" color="text.secondary" display="block">{opts.hint}</Typography> : null}
        {opts.warning ? <Typography variant="caption" color="warning.main" display="block">{opts.warning}</Typography> : null}
      </Box>
    </Box>
  );

  const renderOrderKeys = () => {
    if (!prep) return null;
    const hasAccount = prep.keys.some(x => x.type === 'account');
    return (
      <Box>
        {sectionLabel(k('add.keysLabel'))}
        <Stack spacing={0.75}>
          {prep.keys.map(x => keyRow({
            id: keyId(x),
            label: k(`keyType.${x.type}`),
            value: keyDisplay(x),
            hint: x.usable ? keyHint(x) : null,
            warning: x.usable ? null : x.warning,
            checked: x.usable && checked.has(keyId(x)),
            disabled: !x.usable || !!existing,
            onToggle: () => toggleChecked(keyId(x)),
            tag: x.usable ? undefined : k('add.notUsable'),
          }))}
          {!hasAccount && subject?.platform === 'phone' && keyRow({
            id: 'no-account', label: k('add.noAccount'), hint: k('add.noAccountHint'), checked: false, disabled: true,
          })}
        </Stack>
      </Box>
    );
  };

  const renderEditKeys = () => {
    if (!entry) return null;
    return (
      <Box>
        {sectionLabel(k('add.keysLabel'))}
        <Stack spacing={0.75}>
          {entry.keys.map(x => keyRow({
            id: x.id != null ? String(x.id) : keyId(x),
            label: k(`keyType.${x.type}`),
            value: keyDisplay(x),
            hint: keyHint(x),
            checked: x.id == null || !removeKeyIds.has(x.id),
            disabled: readOnly || x.id == null,
            onToggle: () => { if (x.id != null) toggleRemove(x.id); },
          }))}
          {addPhones.map(v => keyRow({
            id: `new|${v}`, label: k('keyType.phone'), value: v, hint: k('add.phoneHint'), checked: true, disabled: false,
            onToggle: () => setAddPhones(prev => prev.filter(p => p !== v)),
          }))}
        </Stack>
        {!readOnly && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField size="small" label={k('add.phoneLabel')} value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
              inputProps={{ inputMode: 'tel', style: { fontFamily: MONO } }} sx={{ flex: 1 }} />
            <IconButton onClick={addPhone} disabled={!newPhone.trim()} aria-label={k('add.phoneLabel')}><AddIcon /></IconButton>
          </Stack>
        )}
      </Box>
    );
  };

  const reasonChips = (
    <Box>
      {sectionLabel(k('add.reasonLabel'))}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {BLACKLIST_REASONS.map(r => {
          const on = reason === r;
          return (
            <Chip key={r} label={k(`reason.${r}`)} disabled={readOnly && !on}
              onClick={readOnly ? undefined : () => { setReason(r); setError(null); }}
              variant={on ? 'filled' : 'outlined'}
              sx={on ? (theme) => ({
                bgcolor: alpha(theme.palette.error.main, 0.14),
                color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
                fontWeight: 700,
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) },
              }) : undefined} />
          );
        })}
      </Box>
    </Box>
  );

  const fmtReason = (r: BlacklistReason) => k(`reason.${r}`);

  const canSubmit = !saving && !loading && !loadError && !existing && !readOnly && (mode !== 'edit' || !!entry) && (mode !== 'order' || !!prep) && (mode !== 'manual' || !!name.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      {/* Başlık */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>{title}</Typography>
          {subtitle ? <Typography variant="caption" color="text.secondary" noWrap display="block">{subtitle}</Typography> : null}
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ px: 2.5, py: 2, display: 'grid', gap: 2, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <>
            {existing && (
              <Alert severity="info"
                action={onOpenEntry ? (
                  <Button color="inherit" size="small" onClick={() => onOpenEntry(existing.id)}>{k('banner.openEntry')}</Button>
                ) : undefined}>
                <Typography variant="body2" fontWeight={700}>{k('add.alreadyTitle')}</Typography>
                <Typography variant="body2">
                  {k('add.alreadyBody', {
                    name: existing.displayName,
                    reason: fmtReason(existing.reason),
                    date: formatBlacklistDate(existing.createdAt),
                    by: existing.createdByName || '—',
                  })}
                </Typography>
              </Alert>
            )}

            {readOnly && entry && (
              <Alert severity="info">{k('menu.notAllowed', { owner: entry.ownerBranchName || k('companyOwner') })}</Alert>
            )}

            {mode === 'order' && renderOrderKeys()}
            {mode === 'edit' && renderEditKeys()}

            {/* Ad / telefon / adres */}
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <TextField size="small" label={k('add.nameLabel')} value={name} onChange={(e) => { setName(e.target.value); if (mode === 'manual') setExisting(null); }}
                required={mode === 'manual'} disabled={readOnly} inputProps={{ maxLength: 200 }} fullWidth />
              {mode === 'manual' && (
                <>
                  <TextField size="small" label={k('add.phoneLabel')} value={phone} onChange={(e) => { setPhone(e.target.value); setExisting(null); }}
                    inputProps={{ inputMode: 'tel', style: { fontFamily: MONO }, maxLength: 30 }} fullWidth />
                  <TextField size="small" label={k('add.addressLabel')} value={address} onChange={(e) => setAddress(e.target.value)}
                    inputProps={{ maxLength: 500 }} fullWidth />
                </>
              )}
              {mode === 'order' && (prep?.address || subject?.address) && (
                <Typography variant="caption" color="text.secondary">
                  {k('add.addressLabel')}: {prep?.address || subject?.address}
                </Typography>
              )}
              {mode === 'edit' && entry?.address && (
                <Typography variant="caption" color="text.secondary">{k('add.addressLabel')}: {entry.address}</Typography>
              )}
            </Box>

            {reasonChips}

            <TextField size="small" label={k('add.noteLabel')} placeholder={k('add.notePlaceholder')} value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1000))} disabled={readOnly}
              multiline minRows={2} maxRows={6} inputProps={{ maxLength: 1000 }} fullWidth />

            {showScope && (
              <Box>
                {sectionLabel(k('add.scopeLabel'))}
                <ToggleButtonGroup exclusive size="small" value={onlyBranch ? 'branch' : 'all'} disabled={readOnly}
                  onChange={(_, v) => { if (v) setOnlyBranch(v === 'branch'); }}>
                  <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>{k('add.scopeAll')}</ToggleButton>
                  <ToggleButton value="branch" sx={{ textTransform: 'none', px: 2 }}>{k('add.scopeBranch', { branch: scopeBranchName })}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </>
        )}
      </Box>

      {/* Alt çubuk */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving} color="inherit">{k('add.cancel')}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit} sx={blackButtonSx}>
            {saving ? <CircularProgress size={18} color="inherit" /> : mode === 'edit' ? k('add.save') : k('add.submit')}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default AddToBlacklistModal;
